import { useEffect, useState } from "react";
import { Loader2, UserPlus, Check, X } from "lucide-react";
import { api } from "../api";
import { Panel, PageHead, Loading, LoadError, Badge, fmtDate } from "../ui";

const fieldCls =
    "w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors";

export const AdminCoordinators = () => {
    const [users, setUsers] = useState(null);
    const [error, setError] = useState("");
    const [toggling, setToggling] = useState("");
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "COORDINATOR" });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");
    const [created, setCreated] = useState(null);

    const load = () =>
        api
            .get("/admin/coordinators")
            .then((res) => {
                setUsers(res.data.items);
                setError("");
            })
            .catch(() => setError("Could not load the team. Please refresh."));

    useEffect(() => {
        load();
    }, []);

    const toggle = async (u) => {
        setToggling(u.id);
        try {
            await api.patch(`/admin/coordinators/${u.id}`, { active: !u.active });
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || "Could not update this account.");
        } finally {
            setToggling("");
        }
    };

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateError("");
        setCreated(null);
        try {
            const { data } = await api.post("/admin/coordinators", form);
            setCreated(data);
            setForm({ name: "", email: "", password: "", role: "COORDINATOR" });
            await load();
        } catch (err) {
            setCreateError(err.response?.data?.detail || "Could not create the account.");
        } finally {
            setCreating(false);
        }
    };

    if (error && !users) return <LoadError message={error} />;
    if (!users) return <Loading />;

    return (
        <div data-testid="admin-coordinators-page">
            <PageHead title="Staff & Coordinators" subtitle="Manage who can sign in to this console and what they can do." />

            <div className="grid lg:grid-cols-3 gap-6">
                <Panel className="lg:col-span-2 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left" data-testid="coordinators-table">
                            <thead>
                                <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                                    {["Name", "Email", "Role", "Assigned cases", "Status", "Joined", ""].map((h) => (
                                        <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} data-testid={`coordinator-row-${u.email}`} className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors">
                                        <td className="px-4 py-3 text-sm text-[#F7F5EF] font-medium whitespace-nowrap">{u.name}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{u.email}</td>
                                        <td className="px-4 py-3">
                                            <Badge className={u.role === "ADMIN" ? "bg-yellow-950/60 text-yellow-300 border-yellow-800/70" : "border-[#1D322E] bg-slate-800/80 text-slate-300"}>
                                                {u.role}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-emerald-300 font-semibold" data-testid={`coordinator-cases-${u.email}`}>
                                            {u.assigned_cases}
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.active ? (
                                                <Badge className="border-emerald-800/70 bg-emerald-950/60 text-emerald-300">Active</Badge>
                                            ) : (
                                                <Badge className="border-zinc-700/80 bg-zinc-800/80 text-zinc-400">Deactivated</Badge>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                data-testid={`coordinator-active-toggle-${u.email}`}
                                                onClick={() => toggle(u)}
                                                disabled={toggling === u.id}
                                                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                                    u.active
                                                        ? "border-red-900/60 text-red-300 hover:bg-red-950/40"
                                                        : "border-emerald-800/70 text-emerald-300 hover:bg-emerald-950/40"
                                                }`}
                                            >
                                                {toggling === u.id ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : u.active ? (
                                                    <X className="w-3 h-3" />
                                                ) : (
                                                    <Check className="w-3 h-3" />
                                                )}
                                                {u.active ? "Deactivate" : "Activate"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {error && <div className="p-4"><LoadError message={error} /></div>}
                </Panel>

                <Panel className="p-5 h-fit" data-testid="coordinator-create-form">
                    <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                        <UserPlus className="w-4 h-4" /> Add team member
                    </h2>
                    <form onSubmit={create} className="mt-4 space-y-4">
                        <div>
                            <label htmlFor="coord-name" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">Full name</label>
                            <input id="coord-name" data-testid="coordinator-create-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={`mt-1.5 ${fieldCls}`} placeholder="Ananya Nair" />
                        </div>
                        <div>
                            <label htmlFor="coord-email" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">Email</label>
                            <input id="coord-email" data-testid="coordinator-create-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={`mt-1.5 ${fieldCls}`} placeholder="name@healease.health" />
                        </div>
                        <div>
                            <label htmlFor="coord-password" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">Temporary password</label>
                            <input id="coord-password" data-testid="coordinator-create-password" type="text" required minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={`mt-1.5 ${fieldCls}`} placeholder="At least 8 characters" />
                        </div>
                        <div>
                            <label htmlFor="coord-role" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">Role</label>
                            <select id="coord-role" data-testid="coordinator-create-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={`mt-1.5 ${fieldCls}`}>
                                <option value="COORDINATOR">Coordinator</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </div>
                        {createError && (
                            <p role="alert" data-testid="coordinator-create-error" className="rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                                {createError}
                            </p>
                        )}
                        {created && (
                            <p data-testid="coordinator-create-success" className="rounded-lg border border-emerald-900/60 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-300">
                                {created.name} can now sign in with {created.email}.
                            </p>
                        )}
                        <button
                            type="submit"
                            data-testid="coordinator-create-submit"
                            disabled={creating}
                            className="w-full rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {creating ? "Creating…" : "Create account"}
                        </button>
                    </form>
                </Panel>
            </div>
        </div>
    );
};
